import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { READINESS_EXIT_DURATION_MS } from 'modules/overlay-v2/domain/constants/readiness';
import { theme } from 'styles/theme/theme';

/** Уход — те же 700 мс, что и у чеклиста: сначала пауза, потом знак гаснет. */
const EXIT_ANIMATION_MS = 300;
const EXIT_DELAY_MS = READINESS_EXIT_DURATION_MS - EXIT_ANIMATION_MS;

const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -8px); }
  to { opacity: 1; transform: translate(-50%, 0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translate(-50%, 0); }
  to { opacity: 0; transform: translate(-50%, -8px); }
`;

/**
 * Блик — одна полоса, которая бежит сквозь весь знак.
 *
 * У «Im» и у «Game» свои цвета, поэтому градиентов два: на знаке и на вложенном «Im».
 * Чтобы полоса была одна, оба градиента живут в одной системе координат: размер и сдвиг
 * заданы в `em`, а не в процентах (проценты считаются от ширины своего элемента, и полоса
 * в «Im» шла со своей скоростью). «Im» начинается ровно с левого края знака, поэтому при
 * одинаковых `em` полосы совпадают до пикселя.
 *
 * Дорожка шириной `SHIMMER_TRACK_EM` повторяется, полоса — в её середине; знак шириной ~4em, поэтому в кадре
 * всегда одна полоса, а сдвиг на целую дорожку (-6em → 2em) проводит её через знак без рывка на стыке кругов.
 */
const SHIMMER_TRACK_EM = 8;

const shimmer = keyframes`
  from { background-position: -6em 0; }
  to { background-position: 2em 0; }
`;

/** Keyframes Emotion подставляются только прямо в шаблон стилей — в обычной строке имя кадров теряется. */
const SHIMMER_DURATION = '1.8s ease-in-out infinite';

function shimmerGradient(base: string, highlight: string) {
  return `linear-gradient(100deg, ${base} 40%, ${highlight} 50%, ${base} 60%)`;
}

export const LoaderRoot = styled.div<{ isClosing: boolean }>`
  /* Под знаком табло и видео — мышь он не перехватывает. */
  position: fixed;
  top: 24px;
  left: 50%;
  z-index: ${theme.zIndex.modal};
  pointer-events: none;
  transform: translateX(-50%);
  /* Фон прозрачный — под знаком видео из OBS. Тень отделяет буквы от светлой картинки. */
  filter: drop-shadow(0 2px 8px color-mix(in srgb, ${theme.colors.DarkBlue} 60%, transparent));
  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)} ${EXIT_ANIMATION_MS}ms ease-in-out
    ${({ isClosing }) => (isClosing ? EXIT_DELAY_MS : 0)}ms forwards;

  & .text {
    font-size: 32px;
    color: transparent;
    background: ${shimmerGradient(theme.colors.InkOnDark, theme.colors.DarkYellow)};
    background-size: ${SHIMMER_TRACK_EM}em 100%;
    background-clip: text;
    -webkit-background-clip: text;
    animation: ${shimmer} ${SHIMMER_DURATION};
  }

  & .text > span {
    color: transparent;
    background: ${shimmerGradient(theme.colors.DarkYellow, theme.colors.InkOnDark)};
    background-size: ${SHIMMER_TRACK_EM}em 100%;
    background-clip: text;
    -webkit-background-clip: text;
    /* Та же анимация, та же длительность и тот же момент монтирования — полосы идут в такт. */
    animation: ${shimmer} ${SHIMMER_DURATION};
  }

  /* Системная настройка «уменьшить движение»: знак стоит без блика, в обычных цветах. */
  @media (prefers-reduced-motion: reduce) {
    &,
    & * {
      animation: none !important;
    }

    & .text {
      color: ${theme.colors.InkOnDark};
      background: none;
    }

    & .text > span {
      color: ${theme.colors.DarkYellow};
      background: none;
    }
  }
`;
